import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

interface LoginCodeScreenProps {
  showSuccessMessage?: boolean;
  onNavigateBack?: () => void;
}

export function LoginCodeScreen({ showSuccessMessage, onNavigateBack }: LoginCodeScreenProps) {
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    showSuccessMessage ? { type: "success", text: "Code sent! Check your email." } : null
  );
  const { loginWithCode } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsSubmitting(true);
    setMessage(null);

    const result = await loginWithCode(code);

    if (result.success) {
      // Success! User will be automatically redirected by auth state change
      setMessage({
        type: "success",
        text: "Successfully signed in! Redirecting...",
      });
    } else {
      // Show error on current page
      setMessage({
        type: "error",
        text: result.error || "Invalid code. Please check the code and try again.",
      });
    }

    setIsSubmitting(false);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits and limit to 6 characters
    const digits = e.target.value.replace(/[^0-9]/g, "").substring(0, 6);
    setCode(digits);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <div className="max-w-md w-full px-8">
        {/* Main Content */}
        <div className="flex flex-col items-center">
          {/* Logo */}
          <img
            src="/Rebuild_Logo_White.png"
            alt="Rebuild Logo"
            className="h-16 mb-6"
          />
          {/* Title */}
          <h1 className="text-3xl font-bold text-white mb-2">Tools</h1>
          <p className="text-xl text-gray-400 mb-8">Enter your 6-digit code</p>

          {/* Code Form */}
          <form onSubmit={handleSubmit} className="w-full">
            <div className="mb-4">
              <label className="block text-gray-400 mb-2 text-sm">6-Digit Code</label>
              <input
                type="text"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="000000"
                value={code}
                onChange={handleCodeChange}
                disabled={isSubmitting}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>

            {/* Message */}
            {message && (
              <div className="mb-4">
                <p className={`text-center text-sm ${message.type === "success" ? "text-green-400" : "text-red-400"}`}>
                  {message.text}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors mb-4"
              disabled={isSubmitting || code.length !== 6}
            >
              {isSubmitting ? "Signing in..." : "Sign In"}
            </button>

            {/* Back Button */}
            {onNavigateBack && (
              <button
                type="button"
                onClick={onNavigateBack}
                className="w-full px-6 py-3 bg-transparent text-gray-400 font-semibold rounded-lg hover:text-gray-300 transition-colors"
                disabled={isSubmitting}
              >
                Back to email
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
